// frontend/src/components/Assistant.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { askAssistant } from "../features/assistant/assistantThunks.js";
import {
  addUserMessage,
  addAssistantMessage,
  clearAssistantChat,
} from "../features/assistant/assistantSlice.js";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import PageHeader from "../pages/PageHeader.jsx";

import "../styles/assistant.css";

const Assistant = () => {
  const dispatch = useDispatch();
  const { messages, loading, error } = useSelector((state) => state.assistant);
  const auth = useSelector((state) => state.auth || {});

  // Nombre y avatar del usuario (todo opcional, con fallbacks seguros)
  const rawName =
    auth?.user?.first_name ||
    auth?.user?.name ||
    auth?.user?.username ||
    auth?.user?.email ||
    "";
  const displayName = rawName ? rawName.split(" ")[0] : "Paciente";

  const userAvatarUrl =
    auth?.user?.profile_picture || auth?.user?.profilePicture || null;

  const [input, setInput] = useState("");
  const [streamText, setStreamText] = useState("");

  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll al final cuando cambian mensajes o stream (sin crecer el contenedor)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, [messages, streamText]);

  // Mantener el input siempre listo (sin tener que hacer click)
  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading]);

  // Focus inicial al montar
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSend = (event) => {
    event.preventDefault();
    if (!input.trim() || loading) return;

    const userText = input.trim();

    setInput("");
    setStreamText("");

    if (inputRef.current) {
      inputRef.current.focus();
    }

    dispatch(addUserMessage(userText));

    dispatch(
      askAssistant({
        message: userText,
        onChunk: (chunk) =>
          setStreamText((prevStreamText) => prevStreamText + chunk),
      })
    ).then((resultAction) => {
      if (resultAction.meta.requestStatus === "fulfilled") {
        const finalText = (resultAction.payload || "").trim();
        if (finalText) {
          dispatch(addAssistantMessage(finalText));
        }
        setStreamText("");
      }

      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  };

  const handleClear = () => {
    dispatch(clearAssistantChat());
    setStreamText("");
    setInput("");

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="assistant-container">
      <section className="assistant-hero">
        <div className="assistant-hero-text">
          <div className="assistant-hero-greeting">
            <PageHeader />
            <span>{rawName ? `Hola, ${displayName}` : "Hola,"}</span>
          </div>
          <h2 className="assistant-hero-title">¿En qué puedo ayudarte hoy?</h2>
          <p className="assistant-hero-subtitle">
            Puedo resolver dudas sobre tus cirugías, cuidados pre y
            postoperatorios y próximos pasos de tu tratamiento.
          </p>
        </div>

        <div className="assistant-hero-orb">
          <div className="assistant-hero-orb-inner">AI</div>
        </div>
      </section>

      <section className="assistant-chat-card">
        <header className="assistant-chat-header">
          <div className="assistant-chat-header-left">
            <div className="assistant-avatar ai">
              <span>AI</span>
            </div>
            <div>
              <p className="assistant-chat-title">Asistente Clínico</p>
              <p className="assistant-chat-status">
                {loading ? "Escribiendo…" : "Disponible ahora"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClear}
            className="clear-btn"
            disabled={loading}
          >
            Limpiar
          </button>
        </header>

        <div className="assistant-messages" ref={messagesContainerRef}>
          {messages.length === 0 && !streamText && (
            <p className="assistant-placeholder">
              Empieza contándome en qué procedimiento o duda quieres que te
              ayude.
            </p>
          )}

          {messages.map((msg, index) => (
            <div key={index} className={`assistant-message-row ${msg.role}`}>
              {msg.role === "assistant" && (
                <div className="assistant-avatar ai">
                  <span>AI</span>
                </div>
              )}

              <div className={`assistant-message-bubble ${msg.role}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              </div>

              {msg.role === "user" && (
                <div className="assistant-avatar user">
                  {userAvatarUrl ? (
                    <img src={userAvatarUrl} alt="Tu foto de perfil" />
                  ) : (
                    <span>{displayName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
              )}
            </div>
          ))}

          {streamText && (
            <div className="assistant-message-row assistant">
              <div className="assistant-avatar ai">
                <span>AI</span>
              </div>
              <div className="assistant-message-bubble assistant">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {streamText}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {error && <p className="assistant-error">{error}</p>}

        <form onSubmit={handleSend} className="assistant-input-area">
          <input
            ref={inputRef}
            autoFocus
            type="text"
            placeholder={
              loading
                ? "El asistente está respondiendo… (puedes ir escribiendo)"
                : "Escribe tu mensaje…"
            }
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          <button type="submit" disabled={loading || !input.trim()}>
            {loading ? "Enviando…" : "Enviar"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Assistant;